import { useState, useCallback, useRef } from "react";
import { downloadLatestAppApk, getLatestAppDownloadInfo } from "../utils/appDownload";
import { t } from "../utils/i18n";

/** 下载超过该时间才显示进度弹窗，避免缓存命中时闪一下 */
const PROGRESS_SHOW_DELAY_MS = 400;

const EMPTY_MODAL_INFO = {
  loading: false,
  previewUrl: "",
  versionName: "",
  description: "",
};

/**
 * App APK 下载（说明弹窗 + 进度弹层）
 * @param {Object} options
 * @param {import('../Components/Toast/Toast').useToast} options.toast
 * @param {string} [options.comingSoonKey]
 */
export function useAppDownload({
  toast,
  comingSoonKey = "home.app.comingSoon",
} = {}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalInfo, setModalInfo] = useState(EMPTY_MODAL_INFO);
  const [progress, setProgress] = useState({
    active: false,
    visible: false,
    minimized: false,
    phase: "preparing",
    percent: null,
  });
  const busyRef = useRef(false);
  const showTimerRef = useRef(null);

  const clearShowTimer = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  }, []);

  const scheduleProgressVisible = useCallback(() => {
    if (showTimerRef.current) return;
    showTimerRef.current = setTimeout(() => {
      showTimerRef.current = null;
      setProgress(prev =>
        prev.active ? { ...prev, visible: true } : prev,
      );
    }, PROGRESS_SHOW_DELAY_MS);
  }, []);

  const minimizeDownload = useCallback(() => {
    setProgress(prev =>
      prev.visible ? { ...prev, minimized: true } : prev,
    );
  }, []);

  const expandDownload = useCallback(() => {
    setProgress(prev =>
      prev.active ? { ...prev, visible: true, minimized: false } : prev,
    );
  }, []);

  const resetProgress = useCallback(() => {
    clearShowTimer();
    busyRef.current = false;
    setProgress({
      active: false,
      visible: false,
      minimized: false,
      phase: "preparing",
      percent: null,
    });
  }, [clearShowTimer]);

  const download = useCallback(async () => {
    if (busyRef.current) {
      return;
    }

    busyRef.current = true;
    clearShowTimer();
    setProgress({
      active: true,
      visible: false,
      minimized: false,
      phase: "preparing",
      percent: null,
    });

    try {
      const result = await downloadLatestAppApk({
        onPhase: phase => {
          setProgress(prev => ({
            ...prev,
            phase,
            percent: phase === "preparing" ? null : prev.percent,
          }));
          if (phase === "downloading") {
            scheduleProgressVisible();
          }
        },
        onProgress: ({ percent }) => {
          setProgress(prev => ({
            ...prev,
            phase: "downloading",
            percent,
          }));
        },
      });

      if (result === "no-version") {
        toast?.info?.(t(comingSoonKey));
        return;
      }
      if (result === "no-url") {
        toast?.error?.(t("home.download.failed"));
        return;
      }
    } catch (e) {
      console.warn("获取 App 下载链接失败:", e);
      toast?.error?.(t("home.download.failed"));
    } finally {
      resetProgress();
    }
  }, [
    toast,
    comingSoonKey,
    clearShowTimer,
    scheduleProgressVisible,
    resetProgress,
  ]);

  const closeDownloadModal = useCallback(() => {
    setModalOpen(false);
    setModalInfo(EMPTY_MODAL_INFO);
  }, []);

  const openDownloadModal = useCallback(async () => {
    if (busyRef.current) {
      return;
    }

    setModalOpen(true);
    setModalInfo({
      loading: true,
      previewUrl: "",
      versionName: "",
      description: "",
    });

    try {
      const info = await getLatestAppDownloadInfo();
      if (!info.attachmentUuid) {
        closeDownloadModal();
        toast?.info?.(t(comingSoonKey));
        return;
      }
      if (!info.previewUrl) {
        closeDownloadModal();
        toast?.error?.(t("home.download.failed"));
        return;
      }

      setModalInfo({
        loading: false,
        previewUrl: info.previewUrl,
        versionName: info.versionName,
        description: info.description,
      });
    } catch (e) {
      console.warn("获取 App 下载信息失败:", e);
      closeDownloadModal();
      toast?.error?.(t("home.download.failed"));
    }
  }, [toast, comingSoonKey, closeDownloadModal]);

  const startDownload = useCallback(async () => {
    await download();
  }, [download]);

  return {
    openDownloadModal,
    closeDownloadModal,
    startDownload,
    modalOpen,
    modalInfo,
    minimizeDownload,
    expandDownload,
    progress: {
      open: progress.visible,
      minimized: progress.minimized,
      phase: progress.phase,
      percent: progress.percent,
    },
    downloading: progress.active,
  };
}
